import React, { useState, useRef, useCallback } from 'react';
import { Box, Snackbar, Alert, Typography } from '@mui/material';
import PreviewPanel from './PreviewPanel';
import './CameraModal.css';
import CameraCapture from '../../components/CameraCapture';
import { put } from '../../services/s3/storageService';
import { removeBackground } from '@imgly/background-removal';
import { useSettings } from '../../contexts/settingsContext';
import { useCameraCaptures } from '../../contexts/cameraCapturesContext';
import { useFaceMatching } from '../../hooks/useFaceMatching';
import { usePhotoQueue } from '../../hooks/usePhotoQueue';

const style = {
  position: 'fixed',
  top: { xs: 56, sm: 64 }, // Position below main AppBar (56px mobile, 64px desktop)
  left: 0,
  right: 0,
  bottom: 0,
  width: '100vw',
  height: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  boxSizing: 'border-box',
  overflow: 'hidden',
  zIndex: 1200, // Below AppBar (1300) but above other content
};

export default function CameraModal({ open, onClose, username, onExposeFileInput }) {
  // Get camera settings from centralized settings context
  const settings = useSettings();
  const {
    removeBg,
    showBoundingBoxes,
    immediateFaceMatch,
    openSettings,
    autoCreateEventOnAIMatch
  } = settings;

  // Get shared captures from context (includes stealth camera captures)
  const { captures, addCapture, removeCapture, updateCapture } = useCameraCaptures();

  // selected capture index (0 = most recent)
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });
  const [processing, setProcessing] = useState(false);
  const [processingImage, setProcessingImage] = useState(null);
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);

  // Expose file input ref to parent
  React.useEffect(() => {
    if (open && onExposeFileInput) {
      onExposeFileInput(fileInputRef);
    }
  }, [open, onExposeFileInput]);

  // Close on click outside
  React.useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      // Don't close if clicking navigation buttons (camera or photo library)
      const isNavButton = event.target.closest('[aria-label="camera"], [aria-label="photo library"]');
      if (isNavButton) return;

      if (containerRef.current && !containerRef.current.contains(event.target)) {
        onClose();
      }
    };

    // Add listener after a brief delay to avoid immediate close from the open click
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose]);

  // Use face matching hook in interactive mode
  const { matchFace: performFaceMatch } = useFaceMatching({
    silent: false,
    onSuccess: (result, matchedPersons, matchList) => {
      const baseMessage = `Found ${result.matchCount} match(es): ${matchList}`;
      const eventMessage = autoCreateEventOnAIMatch ? ' - MATCH SAVED' : '';
      setSnack({
        open: true,
        severity: 'success',
        message: baseMessage + eventMessage
      });
      // Don't close camera in auto-create mode (continuous capture)
      if (!autoCreateEventOnAIMatch) {
        onClose();
      }
    },
    onNoMatch: () => {
      setSnack({
        open: true,
        severity: 'info',
        message: 'No exact match found. Searching by detected facial attributes...'
      });
      // Don't close camera in auto-create mode
      if (!autoCreateEventOnAIMatch) {
        onClose();
      }
    },
    onError: (err) => {
      setSnack({
        open: true,
        severity: 'error',
        message: 'Face matching failed: ' + (err.message || String(err))
      });
    }
  });

  // Queue processor function
  const processPhotoFromQueue = useCallback(async (item) => {
    const capture = captures.find(c => c.id === item.captureId);
    if (!capture) {
      console.warn('[Queue] Capture not found:', item.captureId);
      return;
    }

    // Mark capture as processing
    updateCapture(capture.id, { processing: true });

    try {
      // Perform face matching without blocking UI
      await performFaceMatch(capture.id, capture.dataUrl, capture.clickPosition || null);
    } catch (err) {
      // Error already handled by hook callbacks
      console.error('[Queue] Processing failed:', err);
    } finally {
      // Mark capture as done processing
      updateCapture(capture.id, { processing: false });
    }
  }, [captures, performFaceMatch, updateCapture]);

  // Initialize photo processing queue
  const photoQueue = usePhotoQueue(processPhotoFromQueue);

  const handleDeleteCapture = (i) => {
    const captureToDelete = captures[i];
    if (captureToDelete) {
      removeCapture(captureToDelete.id);
    }
    setSelectedIdx(prev => {
      if (prev === null) return null;
      if (prev === i) return 0;
      if (prev > i) return prev - 1;
      return prev;
    });
  };

  async function processCapture(dataUrl) {
    if (!removeBg) return dataUrl;

    setProcessing(true);
    setProcessingImage(dataUrl);
    try {
      const blob = await removeBackground(dataUrl);
      const processedUrl = URL.createObjectURL(blob);
      setProcessing(false);
      setProcessingImage(null);
      return processedUrl;
    } catch (err) {
      console.error('Background removal failed', err);
      setSnack({ open: true, severity: 'warning', message: 'Background removal failed, using original image' });
      setProcessing(false);
      setProcessingImage(null);
      return dataUrl;
    }
  }

  async function matchFace(idxArg) {
    const idx = typeof idxArg === 'number' ? idxArg : selectedIdx ?? 0;
    const capture = captures && captures.length > idx ? captures[idx] : null;
    if (!capture) return;

    // Add to queue instead of processing immediately
    photoQueue.enqueue({
      id: `queue-${capture.id}-${Date.now()}`,
      captureId: capture.id
    });
  }

  async function uploadCapturedImage(idxArg) {
    const idx = typeof idxArg === 'number' ? idxArg : selectedIdx ?? 0;
    const capture = captures && captures.length > idx ? captures[idx] : null;
    if (!capture) return;
    try {
      setUploading(true);
      const res = await fetch(capture.dataUrl);
      const blob = await res.blob();
      const filename = `profile-${username || 'anonymous'}.png`;
      await put(filename, blob, { contentType: blob.type });
      setSnack({ open: true, severity: 'success', message: 'Image uploaded' });
      setUploading(false);
      onClose();
    } catch (err) {
      console.error('Upload failed', err);
      setSnack({ open: true, severity: 'error', message: 'Upload failed: ' + (err.message || String(err)) });
      setUploading(false);
    }
  }

  // Don't render if not open
  if (!open) return null;

  return (
    <>
      <Box ref={containerRef} sx={style}>
        {/* main area: camera preview and captured preview - responsive layout, no scrollbars */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'flex-start', justifyContent: captures?.length > 0 ? 'flex-start' : 'center', overflow: 'hidden', px: 1, position: 'relative' }}>
          {/* camera area */}
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', height: '100%' }}>
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              <CameraCapture onCapture={(d, clickPosition) => {
                if (!d) return;
                // Process in background without blocking
                processCapture(d).then((processed) => {
                  addCapture(processed, 'camera', clickPosition);
                  setSelectedIdx(0);
                });
              }}
              uploading={false}
              fileInputRef={fileInputRef}
              />
            </Box>
          </Box>
          {/* preview area: sized at ~70% of the camera area (flex: 0.7) */}
          {(captures?.length > 0 || processing) && (
            <PreviewPanel
              captures={captures}
              selectedIdx={selectedIdx}
              onSelect={setSelectedIdx}
              onUpload={uploadCapturedImage}
              onMatch={matchFace}
              onDelete={handleDeleteCapture}
              uploading={false}
              processing={processing}
              processingImage={processingImage}
              showBoundingBoxes={showBoundingBoxes}
              immediateFaceMatch={immediateFaceMatch}
              fileInputRef={fileInputRef}
            />
          )}
        </Box>
      </Box>
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{ top: '50% !important', transform: 'translateY(-50%)' }}
      >
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
